import React, { useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../../components/shared/Buttons"
import { Divider } from "../../../components/shared/Layout"
import { useToast } from "../../../contexts/ToastContext"

const UPDATE_EXTERNAL_CALENDAR_BLOCKING_MUTATION = `
  mutation UpdateExternalCalendarBlocking($externalCalendarAllDayBlocking: ExternalCalendarAllDayBlockingEnum!) {
    updateExternalCalendarBlocking(externalCalendarAllDayBlocking: $externalCalendarAllDayBlocking) {
      result
      errors
      externalCalendarAllDayBlocking
    }
  }
`

type BlockingPolicy = "block_all" | "busy_and_out_of_office_only"

type Props = {
  externalCalendarAllDayBlocking: BlockingPolicy
}

const OPTIONS: { value: BlockingPolicy; title: string; description: string }[] = [
  {
    value: "block_all",
    title: "Block all all-day events",
    description:
      "Any all-day event on your connected calendars blocks booking for those days. Best when you use all-day entries for time off."
  },
  {
    value: "busy_and_out_of_office_only",
    title: "Only block busy & out-of-office all-day events",
    description:
      "All-day reminders and notes marked free in Google Calendar stay visible but do not block booking. Out-of-office and busy all-day events still block."
  }
]

const AllDayBlockingSettings = ({ externalCalendarAllDayBlocking }: Props) => {
  const [selectedPolicy, setSelectedPolicy] = useState<BlockingPolicy>(externalCalendarAllDayBlocking)
  const [saving, setSaving] = useState(false)
  const [, updateBlocking] = useMutation(UPDATE_EXTERNAL_CALENDAR_BLOCKING_MUTATION)
  const { showToast } = useToast()

  const save = () => {
    setSaving(true)
    updateBlocking({ externalCalendarAllDayBlocking: selectedPolicy }).then((result) => {
      setSaving(false)
      const payload = result.data?.updateExternalCalendarBlocking
      if (payload?.result === "success") {
        showToast("All-day blocking preference saved")
        return
      }

      showToast({
        content: payload?.errors?.join(", ") || "There was an error saving your preference",
        type: "error"
      })
    })
  }

  return (
    <div>
      <div className="font-bold">All-day imported events</div>
      <p className="mt-1 text-sm text-gray-dark">
        Choose how all-day events from Google, Outlook, and other connected calendars affect online booking.
        Timed events always follow their busy/free status.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        {OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer gap-3 rounded-lg border border-gray p-4 hover:border-teal">
            <input
              type="radio"
              name="external-calendar-all-day-blocking"
              className="mt-1"
              checked={selectedPolicy === option.value}
              onChange={() => setSelectedPolicy(option.value)}
            />
            <span>
              <span className="block font-semibold">{option.title}</span>
              <span className="block text-sm text-gray-dark">{option.description}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving || selectedPolicy === externalCalendarAllDayBlocking}
          floatingInMobile={false}
          className=""
          style={{}}>
          Save
        </Button>
      </div>
      <Divider className="mt-6" />
    </div>
  )
}

export default AllDayBlockingSettings
